/// <amd-module name="Wit/Common/WiqlOperators" />

import { format, localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { dateToString } from "Wit/Common/DateUtils";
import { Dictionary } from "Wit/Common/Dictionary";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import * as WITCommonResources from "Wit/Common/Resources";

export namespace WiqlOperators {
    export const NotSpace = "NOT";
    export const OperatorAnd = "AND";
    export const OperatorOr = "OR";
    export const OperatorContains = "CONTAINS";
    export const OperatorNotContains = "NOT CONTAINS";
    export const OperatorContainsWords = "CONTAINS WORDS";
    export const OperatorNotContainsWords = "NOT CONTAINS WORDS";
    export const OperatorIn = "IN";
    export const OperatorNotIn = "NOT IN";
    export const OperatorEver = "EVER";
    export const OperatorNotEver = "NOT EVER";
    export const OperatorUnder = "UNDER";
    export const OperatorNotUnder = "NOT UNDER";
    export const OperatorInGroup = "IN GROUP";
    export const OperatorNotInGroup = "NOT IN GROUP";
    export const OperatorEqualTo = "=";
    export const OperatorNotEqualTo = "<>";
    export const OperatorIsEmpty = "IS EMPTY";
    export const OperatorIsNotEmpty = "IS NOT EMPTY";
    export const OperatorGreaterThan = ">";
    export const OperatorLessThan = "<";
    export const OperatorGreaterThanOrEqualTo = ">=";
    export const OperatorLessThanOrEqualTo = "<=";
    export const MacroStart = "@";
    export const MacroToday = "@today";
    export const MacroMe = "@me";
    export const MacroProject = "@project";
    export const MacroCurrentIteration = "@currentIteration";
    export const MacroTeamAreas = "@teamAreas";
    export const MacroFollows = "@follows";
    export const MacroRecentMentions = "@recentMentions";
    export const MacroMyRecentActivity = "@myRecentActivity";
    export const MacroRecentProjectActivity = "@recentProjectActivity";
    export const MacroStartOfYear = "@startOfYear";
    export const MacroStartOfMonth = "@startOfMonth";
    export const MacroStartOfWeek = "@startOfWeek";
    export const MacroStartOfDay = "@startOfDay";
}

const invariantToLocalizedMap: Dictionary<string> = {};
const localizedToInvariantMap: Dictionary<string> = {};
const fieldComparisonOperators: Dictionary<string> = {};

export const ProjectOperators = [
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo,
    WiqlOperators.OperatorIn,
    WiqlOperators.OperatorNotIn
];

export const StringOperators = [
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo,
    WiqlOperators.OperatorGreaterThan,
    WiqlOperators.OperatorLessThan,
    WiqlOperators.OperatorGreaterThanOrEqualTo,
    WiqlOperators.OperatorLessThanOrEqualTo,
    WiqlOperators.OperatorContains,
    WiqlOperators.OperatorNotContains,
    WiqlOperators.OperatorIn,
    WiqlOperators.OperatorNotIn,
    WiqlOperators.OperatorInGroup,
    WiqlOperators.OperatorNotInGroup
];

export const InGroupOperators = [WiqlOperators.OperatorInGroup, WiqlOperators.OperatorNotInGroup];
export const TextOperators = [WiqlOperators.OperatorContains, WiqlOperators.OperatorNotContains];
export const HistoryOperators = [WiqlOperators.OperatorContains, WiqlOperators.OperatorNotContains];
export const TextWithTextSupportOperators = [WiqlOperators.OperatorContainsWords, WiqlOperators.OperatorNotContainsWords];
export const HistoryWithTextSupportOperators = [WiqlOperators.OperatorContainsWords, WiqlOperators.OperatorNotContainsWords];
const isEmptyOperators = [WiqlOperators.OperatorIsEmpty, WiqlOperators.OperatorIsNotEmpty];
TextOperators.push(...isEmptyOperators);
TextWithTextSupportOperators.push(...isEmptyOperators);

export const StringWithTextSupportOperators = StringOperators.concat(TextWithTextSupportOperators);
export const StringWithInGroupOperators = StringOperators; // TODO: We need to separate to have only ingroup operators. #1366312

export const TreePathOperators = [
    WiqlOperators.OperatorUnder,
    WiqlOperators.OperatorNotUnder,
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo,
    WiqlOperators.OperatorIn,
    WiqlOperators.OperatorNotIn
];

export const AreaPathOperatorsSupportingMacros = [WiqlOperators.OperatorEqualTo, WiqlOperators.OperatorNotEqualTo];

export const IterationPathOperatorsSupportingMacros = [
    WiqlOperators.OperatorUnder,
    WiqlOperators.OperatorNotUnder,
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo
];

export const ComparisonOperators = [
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo,
    WiqlOperators.OperatorGreaterThan,
    WiqlOperators.OperatorLessThan,
    WiqlOperators.OperatorGreaterThanOrEqualTo,
    WiqlOperators.OperatorLessThanOrEqualTo,
    WiqlOperators.OperatorIn,
    WiqlOperators.OperatorNotIn
];

export const EqualityOperators = [WiqlOperators.OperatorEqualTo, WiqlOperators.OperatorNotEqualTo];

const GroupOperators: Dictionary<boolean> = {};
InGroupOperators.forEach(op => {
    GroupOperators[op.toUpperCase()] = true;
});

const MultipleValueOperators: Dictionary<boolean> = {};
[WiqlOperators.OperatorIn, WiqlOperators.OperatorNotIn].forEach(op => {
    MultipleValueOperators[op.toUpperCase()] = true;
});

export const OperatorsSupportingFieldComparison: { [op: string]: boolean } = {};
[
    WiqlOperators.OperatorEqualTo,
    WiqlOperators.OperatorNotEqualTo,
    WiqlOperators.OperatorGreaterThan,
    WiqlOperators.OperatorLessThan,
    WiqlOperators.OperatorGreaterThanOrEqualTo,
    WiqlOperators.OperatorLessThanOrEqualTo
].forEach(op => {
    OperatorsSupportingFieldComparison[op.toUpperCase()] = true;
});

export const FieldTypesSupportingEver: Dictionary<boolean> = {};
[FieldType.String, FieldType.Integer, FieldType.Double, FieldType.Boolean, FieldType.Guid].forEach(ft => {
    FieldTypesSupportingEver[ft] = true;
});

export const FieldTypesSupportingFieldComparison: Dictionary<boolean> = {};
[FieldType.String, FieldType.DateTime, FieldType.Integer, FieldType.Double, FieldType.Boolean, FieldType.Guid].forEach(ft => {
    FieldTypesSupportingFieldComparison[ft] = true;
});

const IdentityFieldStringOperators: Dictionary<boolean> = {};
[
    WiqlOperators.OperatorContains,
    WiqlOperators.OperatorContainsWords,
    WiqlOperators.OperatorGreaterThan,
    WiqlOperators.OperatorGreaterThanOrEqualTo,
    WiqlOperators.OperatorLessThan,
    WiqlOperators.OperatorLessThanOrEqualTo,
    WiqlOperators.OperatorNotContains,
    WiqlOperators.OperatorNotContainsWords,
    WiqlOperators.OperatorNotUnder,
    WiqlOperators.OperatorUnder
].forEach(op => {
    IdentityFieldStringOperators[op.toUpperCase()] = true;
});

function mapOperatorPair(invariantOperator: string, localizedOperator: string) {
    invariantToLocalizedMap[invariantOperator.toUpperCase()] = localizedOperator;
    localizedToInvariantMap[localizedOperator.toLocaleUpperCase()] = invariantOperator;
}

export function getLocalizedOperator(invariantOperator: string): string {
    return invariantToLocalizedMap[invariantOperator.trim().toUpperCase()] || invariantOperator;
}

export function getLocalizedOperatorList(invariantOperators: string[]) {
    return invariantOperators.map(iop => getLocalizedOperator(iop));
}

export function isFieldComparisonOperator(localizedOperator: string): boolean {
    return fieldComparisonOperators.hasOwnProperty(localizedOperator.trim().toLocaleUpperCase());
}

export function getFieldComparisonOperator(invariantOperator: string): string {
    return getLocalizedOperator(invariantOperator) + WITCommonResources.WiqlOperators_FieldComparisonQualifier;
}

export function isTodayMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return startsWith(macro, getLocalizedOperator(WiqlOperators.MacroToday), true);
    }

    return startsWith(macro, WiqlOperators.MacroToday, true);
}

export function isStartOfDayMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return startsWith(macro, getLocalizedOperator(WiqlOperators.MacroStartOfDay), true);
    }
    return startsWith(macro, WiqlOperators.MacroStartOfDay, true);
}

export function isMeMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return localeIgnoreCaseComparer(macro, getLocalizedOperator(WiqlOperators.MacroMe)) === 0;
    }
    return localeIgnoreCaseComparer(macro, WiqlOperators.MacroMe) === 0;
}

export function isCurrentIterationMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return startsWith(macro, getLocalizedOperator(WiqlOperators.MacroCurrentIteration), true);
    }
    return startsWith(macro, WiqlOperators.MacroCurrentIteration, true);
}

export function isTeamAreasMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return startsWith(macro, getLocalizedOperator(WiqlOperators.MacroTeamAreas), true);
    }
    return startsWith(macro, WiqlOperators.MacroTeamAreas, true);
}

export function isProjectMacro(macro: string, localized: boolean): boolean {
    if (localized) {
        return localeIgnoreCaseComparer(macro, getLocalizedOperator(WiqlOperators.MacroProject)) === 0;
    }
    return localeIgnoreCaseComparer(macro, WiqlOperators.MacroProject) === 0;
}

export function isOrOperator(operator: string, localized: boolean): boolean {
    if (localized) {
        return localeIgnoreCaseComparer(operator, getLocalizedOperator(WiqlOperators.OperatorOr)) === 0;
    }
    return localeIgnoreCaseComparer(operator, WiqlOperators.OperatorOr) === 0;
}

export function getInvariantTodayMacro(localizedMacro: string): string {
    return WiqlOperators.MacroToday + localizedMacro.substring(getLocalizedOperator(WiqlOperators.MacroToday).length);
}

export function getInvariantCurrentIterationMacro(localizedMacro: string): string {
    return WiqlOperators.MacroCurrentIteration + localizedMacro.substring(getLocalizedOperator(WiqlOperators.MacroCurrentIteration).length);
}

export function getInvariantStartOfDayMacro(localizedMacro: string): string {
    return WiqlOperators.MacroStartOfDay + localizedMacro.substring(getLocalizedOperator(WiqlOperators.MacroStartOfDay).length);
}

export function getLocalizedTodayMacro(invariantMacro: string): string {
    const localizedTodayMacro = getLocalizedOperator(WiqlOperators.MacroToday);
    const invariantTodayMacro = getInvariantTodayMacro(WiqlOperators.MacroToday);
    return localizedTodayMacro + invariantMacro.substring(invariantTodayMacro.length);
}

export function getLocalizedTodayMinusMacro(num: number): string {
    return format("{0} - {1}", getLocalizedOperator(WiqlOperators.MacroToday), num);
}

export function getLocalizedTodayPlusMacro(num: number): string {
    return format("{0} + {1}", getLocalizedOperator(WiqlOperators.MacroToday), num);
}

export function getLocalizedStartOfDayMacro(num: number): string {
    return format("{0}('{1}d')", getLocalizedOperator(WiqlOperators.MacroStartOfDay), num);
}

export function isValidMacro(macro: string, localized: boolean): boolean {
    if (!startsWith(macro.trim(), WiqlOperators.MacroStart, true)) {
        return false;
    }
    return true;
}

export function getInvariantOperator(localizedOperator: string): string {
    const invariantOperator = localizedToInvariantMap[localizedOperator.trim().toLocaleUpperCase()];

    if (!invariantOperator) {
        if (isTodayMacro(localizedOperator, true)) {
            return getInvariantTodayMacro(localizedOperator);
        } else if (isCurrentIterationMacro(localizedOperator, true)) {
            return getInvariantCurrentIterationMacro(localizedOperator);
        } else if (isStartOfDayMacro(localizedOperator, true)) {
            return getInvariantStartOfDayMacro(localizedOperator);
        }
    } else {
        return invariantOperator;
    }

    return localizedOperator;
}

export function isGroupOperator(localizedOperator: string): boolean {
    return GroupOperators.hasOwnProperty(getInvariantOperator(localizedOperator).toUpperCase());
}

export function isContainsNotContainsOperator(localizedOperator: string): boolean {
    return (
        getInvariantOperator(localizedOperator) === WiqlOperators.OperatorContains ||
        getInvariantOperator(localizedOperator) === WiqlOperators.OperatorNotContains
    );
}

export function isMultipleValueOperator(localizedOperator: string): boolean {
    return MultipleValueOperators.hasOwnProperty(getInvariantOperator(localizedOperator).toUpperCase());
}

export function isIdentityFieldStringOperator(localizedOperator: string): boolean {
    return IdentityFieldStringOperators.hasOwnProperty(getInvariantOperator(localizedOperator).toUpperCase());
}

export function getLocalizedCurrentIterationMacroPlusMinus(num: string): string {
    return format("{0} {1}", getLocalizedOperator(WiqlOperators.MacroCurrentIteration), num);
}

export function getLocalizedCurrentIterationMacro(invariantMacro: string): string {
    const localizedIterationMacro = getLocalizedOperator(WiqlOperators.MacroCurrentIteration);
    const invariantIterationMacro = getInvariantTodayMacro(WiqlOperators.MacroCurrentIteration);
    return localizedIterationMacro + invariantMacro.substring(invariantIterationMacro.length);
}

mapOperatorPair(WiqlOperators.OperatorAnd, WITCommonResources.WiqlOperators_And);
mapOperatorPair(WiqlOperators.OperatorOr, WITCommonResources.WiqlOperators_Or);
mapOperatorPair(WiqlOperators.OperatorContains, WITCommonResources.WiqlOperators_Contains);
mapOperatorPair(WiqlOperators.OperatorNotContains, WITCommonResources.WiqlOperators_NotContains);
mapOperatorPair(WiqlOperators.OperatorContainsWords, WITCommonResources.WiqlOperators_ContainsWords);
mapOperatorPair(WiqlOperators.OperatorNotContainsWords, WITCommonResources.WiqlOperators_NotContainsWords);
mapOperatorPair(WiqlOperators.OperatorIn, WITCommonResources.WiqlOperators_In);
mapOperatorPair(WiqlOperators.OperatorNotIn, WITCommonResources.WiqlOperators_NotIn);
mapOperatorPair(WiqlOperators.OperatorEver, WITCommonResources.WiqlOperators_Ever);
mapOperatorPair(WiqlOperators.OperatorNotEver, WITCommonResources.WiqlOperators_NotEver);
mapOperatorPair(WiqlOperators.OperatorUnder, WITCommonResources.WiqlOperators_Under);
mapOperatorPair(WiqlOperators.OperatorNotUnder, WITCommonResources.WiqlOperators_NotUnder);
mapOperatorPair(WiqlOperators.OperatorEqualTo, WITCommonResources.WiqlOperators_EqualTo);
mapOperatorPair(WiqlOperators.OperatorNotEqualTo, WITCommonResources.WiqlOperators_NotEqualTo);
mapOperatorPair(WiqlOperators.OperatorIsEmpty, WITCommonResources.WiqlOperators_IsEmpty);
mapOperatorPair(WiqlOperators.OperatorIsNotEmpty, WITCommonResources.WiqlOperators_IsNotEmpty);
mapOperatorPair(WiqlOperators.OperatorGreaterThan, WITCommonResources.WiqlOperators_GreaterThan);
mapOperatorPair(WiqlOperators.OperatorLessThan, WITCommonResources.WiqlOperators_LessThan);
mapOperatorPair(WiqlOperators.OperatorGreaterThanOrEqualTo, WITCommonResources.WiqlOperators_GreaterThanOrEqualTo);
mapOperatorPair(WiqlOperators.OperatorLessThanOrEqualTo, WITCommonResources.WiqlOperators_LessThanOrEqualTo);
mapOperatorPair(WiqlOperators.OperatorInGroup, WITCommonResources.WiqlOperators_InGroup);
mapOperatorPair(WiqlOperators.OperatorNotInGroup, WITCommonResources.WiqlOperators_NotInGroup);
mapOperatorPair(WiqlOperators.MacroToday, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroToday);
mapOperatorPair(WiqlOperators.MacroMe, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroMe);
mapOperatorPair(WiqlOperators.MacroProject, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroProject);
mapOperatorPair(WiqlOperators.MacroCurrentIteration, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroCurrentIteration);
mapOperatorPair(WiqlOperators.MacroTeamAreas, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroTeamAreas);
mapOperatorPair(WiqlOperators.MacroFollows, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroFollows);
mapOperatorPair(WiqlOperators.MacroRecentMentions, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroRecentMentions);
mapOperatorPair(WiqlOperators.MacroMyRecentActivity, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroMyRecentActivity);
mapOperatorPair(WiqlOperators.MacroRecentProjectActivity, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroRecentProjectActivity);
mapOperatorPair(WiqlOperators.MacroStartOfYear, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroStartOfYear);
mapOperatorPair(WiqlOperators.MacroStartOfMonth, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroStartOfMonth);
mapOperatorPair(WiqlOperators.MacroStartOfWeek, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroStartOfWeek);
mapOperatorPair(WiqlOperators.MacroStartOfDay, WiqlOperators.MacroStart + WITCommonResources.WiqlOperators_MacroStartOfDay);

for (const op in OperatorsSupportingFieldComparison) {
    const fieldComparisonOperator = getFieldComparisonOperator(op);

    localizedToInvariantMap[fieldComparisonOperator.toLocaleUpperCase()] = op;
    fieldComparisonOperators[fieldComparisonOperator.toLocaleUpperCase()] = op;
}

export const DateMacros = [
    getLocalizedOperator(WiqlOperators.MacroStartOfDay),
    getLocalizedStartOfDayMacro(-1),
    getLocalizedStartOfDayMacro(-7),
    getLocalizedStartOfDayMacro(-30),
    getLocalizedOperator(WiqlOperators.MacroStartOfWeek),
    getLocalizedOperator(WiqlOperators.MacroStartOfMonth),
    getLocalizedOperator(WiqlOperators.MacroStartOfYear)
];

export const TodayMacros = [getLocalizedOperator(WiqlOperators.MacroToday), getLocalizedTodayMinusMacro(1), getLocalizedTodayPlusMacro(1)];

export const IterationPathMacros = [
    getLocalizedOperator(WiqlOperators.MacroCurrentIteration),
    getLocalizedCurrentIterationMacroPlusMinus("+ 1"),
    getLocalizedCurrentIterationMacroPlusMinus("- 1")
];

export const PathMacros = [...IterationPathMacros, getLocalizedOperator(WiqlOperators.MacroTeamAreas)];

export const IdInNotInMacros = [
    getLocalizedOperator(WiqlOperators.MacroFollows),
    getLocalizedOperator(WiqlOperators.MacroRecentMentions),
    getLocalizedOperator(WiqlOperators.MacroMyRecentActivity),
    getLocalizedOperator(WiqlOperators.MacroRecentProjectActivity)
];

export function isNonNullableField(fieldRefName: string): boolean {
    return fieldRefName === CoreFieldRefNames.State || fieldRefName === CoreFieldRefNames.WorkItemType || fieldRefName === CoreFieldRefNames.LinkType;
}

export function quoteString(localizedValue: string): string {
    return "'" + (localizedValue || "").replace(/\'/g, "''") + "'";
}

export function toInvariantDateString(date: Date): string {
    // format is yyyy-MM-ddTHH:mm:ss.fffffffZ
    // Query editor uses day precision so the parsed date is an utc date
    return dateToString(date, false, "yyyy-MM-ddT00:00:00.0000000");
}
